import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { useMemo } from "react";
import { useQuery } from "@tanstack/react-query";
import { useServerFn } from "@tanstack/react-start";
import { listRmas } from "@/lib/returns.functions";
import { ScanInput } from "@/components/ui/scan-input";
import { RfShell, RfRow, RfEmpty, RfScopeMissing } from "@/components/rf/rf-shell";
import { useScope } from "@/hooks/use-scope";
import { StatusPill } from "@/components/ui/status-pill";
import { toast } from "sonner";

export const Route = createFileRoute("/_authenticated/app/rf/returns")({
  component: RfReturns,
});

function RfReturns() {
  const { tenantId, facilityId, customerId } = useScope();
  const nav = useNavigate();
  const listFn = useServerFn(listRmas);

  const { data: rmas = [], isLoading } = useQuery({
    queryKey: ["rf-rmas", tenantId, facilityId, customerId],
    enabled: !!tenantId && !!facilityId,
    queryFn: () =>
      listFn({ data: { tenantId: tenantId!, facilityId: facilityId!, customerId: customerId ?? null } }),
  });

  const open = useMemo(
    () => (rmas as any[]).filter((r) => r.status !== "closed" && r.status !== "cancelled"),
    [rmas],
  );

  function handleScan(code: string) {
    const match = (rmas as any[]).find(
      (r) => (r.rma_number ?? "").toLowerCase() === code.toLowerCase() || r.id === code,
    );
    if (!match) {
      toast.error(`No open return matches "${code}"`);
      return;
    }
    nav({ to: "/app/rf/returns/$rmaId", params: { rmaId: match.id } });
  }

  if (!tenantId || !facilityId) {
    return (
      <RfShell title="Returns" back>
        <RfScopeMissing />
      </RfShell>
    );
  }

  return (
    <RfShell title="Returns" back>
      <ScanInput onScan={handleScan} placeholder="Scan RMA number…" size="lg" />
      <div className="text-xs text-muted-foreground px-1">
        {isLoading ? "Loading…" : `${open.length} open`}
      </div>
      <div className="space-y-2">
        {open.length === 0 && !isLoading ? (
          <RfEmpty>No open returns at this facility.</RfEmpty>
        ) : (
          open.map((r) => (
            <RfRow
              key={r.id}
              to={`/app/rf/returns/${r.id}` as any}
              primary={<span className="font-mono">{r.rma_number ?? r.id.slice(0, 8)}</span>}
              secondary={r.reason ?? "—"}
              trailing={<StatusPill tone="info">{r.status}</StatusPill>}
            />
          ))
        )}
      </div>
    </RfShell>
  );
}